import React, { useState } from 'react';

import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { Order } from '@luxuryescapes/contract-svc-order';

import {
  cancelPreviewPeriod,
  cancelSubscriptionPeriod,
  hasActiveLuxPlusSubscriptionRenewal,
  updateCustomerMembershipSubscriptions,
} from '~/services/MembershipService';
import { formatDateSlashes } from '~/services/TimeService';

import isProd from '~/utils/isProd';

import ErrorDisplay from '../Common/ErrorDisplay';
import NewRefundModal from '../Refund/NewModal';

import LuxPlusRenewalModal from './LuxPlusRenewalModal';
import SubscriptionCancelRefundModal from './SubscriptionCancelRefundModal';
import { checkCanRedeemLuxPlusBenefits } from './utils';

interface Props {
  subscriptions: App.MembershipSubscriptions | null;
  customerId: string;
  currentSubscriptionOrderItem: Order.SubscriptionItem;
  currentSubscriptionOrder: App.Order;
  refreshData: () => Promise<void>;
}

const cancelRenewalReasons = [
  'The annual fee is too expensive',
  'I don’t travel frequently enough',
  'The benefits are not meeting my expectations',
  'I don’t want to be automatically charged',
];

export default function MembershipSubscriptionDetails({
  subscriptions,
  customerId,
  currentSubscriptionOrderItem,
  currentSubscriptionOrder,
  refreshData,
}: Props) {
  const [openCancelRefundModal, setOpenCancelRefundModal] = useState<boolean>(false);
  const [openRefundModal, setOpenRefundModal] = useState<boolean>(false);
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState<boolean>(false);
  const [openCancelTestSubscriptionModal, setOpenCancelTestSubscriptionModal] = useState<boolean>(false);
  const [openResumeSubscriptionModal, setOpenResumeSubscriptionModal] = useState<boolean>(false);
  const [openCancelPreviewPeriodModal, setOpenCancelPreviewPeriodModal] = useState<boolean>(false);
  const [error, setError] = useState<Error>(null);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [explanation, setExplanation] = useState<string | undefined>(undefined);

  if (!subscriptions) {
    return (
      <Box mt={2}>
        <ErrorDisplay message="LuxPlus membership data is unavailable for this member." severity="warning" />
      </Box>
    );
  }

  const hasActiveRenewal = hasActiveLuxPlusSubscriptionRenewal(subscriptions);
  const subscriptionPeriods = subscriptions.subscriptionPeriods ?? [];
  const joiningFeeItem = currentSubscriptionOrder?.subscription_items?.find((item) => item.sub_type === 'joining_fee');
  const isActivePreviewSubscription = subscriptions.tier === 'free_preview' && subscriptions.status === 'ACTIVE';

  const today = dayjs().startOf('day');

  // Sort periods by endDate (descending) to get latest first
  const sortedPeriods = [...subscriptionPeriods].sort(
    (a, b) => dayjs(b.endDate).valueOf() - dayjs(a.endDate).valueOf(),
  );
  const latestPeriod = sortedPeriods[0] ?? null;
  const previousPeriod = sortedPeriods[1] ?? null;

  // Determine which period to show in the renewal snapshot:
  // 1. If latest period endDate is within next 14 days (expiring soon) → show latest
  // 2. If latest period endDate is up to 14 days in past and no newer period → show latest
  // 3. If latest period startDate is within last 14 days (recently renewed) → show previous
  const latestPeriodStartDate = latestPeriod ? dayjs(latestPeriod.startDate).startOf('day') : null;
  const daysSinceLatestStart = latestPeriodStartDate?.isValid() ? today.diff(latestPeriodStartDate, 'day') : null;

  // Check if this is a recently renewed subscription (new period started within 14 days)
  const isRecentlyRenewed =
    daysSinceLatestStart !== null && daysSinceLatestStart >= 0 && daysSinceLatestStart <= 14 && previousPeriod !== null;

  // Determine which period to use for the snapshot
  const snapshotPeriod = isRecentlyRenewed ? previousPeriod : latestPeriod;

  const hasLuxPlusSubscription = Boolean(subscriptions?.expiryDate || subscriptionPeriods.length);

  // Calculate total saved for the snapshot period from its redeemed benefits
  const totalSaved =
    snapshotPeriod?.redeemedBenefits?.reduce((sum, benefit) => sum + (benefit.adjustment ?? 0), 0) ?? 0;

  const membershipFee = currentSubscriptionOrderItem?.total != null ? Number(currentSubscriptionOrderItem.total) : 0;
  const profit = totalSaved - membershipFee;

  const snapshotExpiryDate = snapshotPeriod?.endDate ?? subscriptions?.expiryDate;

  const handleRefundJoiningFeeToggle = () => {
    setOpenRefundModal((prevState) => !prevState);
  };

  const handleCancelSubscriptionToggle = () => {
    if (error) {
      setError(null);
    }
    setOpenCancelSubscriptionModal((prevState) => !prevState);
    setReason(undefined);
    setExplanation(undefined);
  };

  const handleResumeSubscriptionToggle = () => {
    if (error) {
      setError(null);
    }
    setOpenResumeSubscriptionModal((prevState) => !prevState);
  };

  const handleCancelPreviewToggle = () => {
    if (error) {
      setError(null);
    }
    setOpenCancelPreviewPeriodModal((prevState) => !prevState);
  };

  const handleCancelTestSubscriptionToggle = () => {
    setOpenCancelTestSubscriptionModal((prevState) => !prevState);
  };

  const handleProceedCancelTestSubscription = () => {
    try {
      const activeSubscriptionPeriod = subscriptionPeriods.find((period) => period.status === 'ACTIVE');

      if (!activeSubscriptionPeriod) {
        setError(new Error('No active subscription period found.'));
        return;
      }

      cancelSubscriptionPeriod(activeSubscriptionPeriod.id);
      refreshData();
    } catch (error) {
      setError(error);
    }
  };

  const handleCancelPreviewPeriod = () => {
    try {
      cancelPreviewPeriod({ customerId: customerId, op: 'cancel' });
      refreshData();
    } catch (error) {
      setError(error);
    } finally {
      setOpenCancelPreviewPeriodModal(false);
    }
  };

  const handleUpdateMembershipRenewal = async () => {
    try {
      const op = hasActiveRenewal ? 'pause' : 'resume';
      await updateCustomerMembershipSubscriptions({
        customerId,
        op,
        reason:
          op === 'pause'
            ? {
                category: reason,
                explanation: reason === 'Other' ? explanation : undefined,
              }
            : undefined,
      });
      refreshData();
    } catch (error) {
      setError(error);
    } finally {
      setOpenCancelSubscriptionModal(false);
      setOpenResumeSubscriptionModal(false);
    }
  };

  const benefitColumns: Array<GridColDef> = [
    { field: 'itemType', headerName: 'Item type', width: 200, display: 'flex' },
    { field: 'type', headerName: 'Benefit type', width: 200, display: 'flex' },
    { field: 'subtype', headerName: 'Benefit sub-type', width: 200, display: 'flex' },
    {
      field: 'orderId',
      headerName: 'Link to order',
      width: 120,
      renderCell: (params) => (
        <Button target="_blank" href={`/purchases/${params.value}`}>
          View Order
        </Button>
      ),
      display: 'flex',
    },
  ];

  const redeemedBenefits = subscriptionPeriods.flatMap((period) => period.redeemedBenefits ?? []);

  return (
    <>
      <LuxPlusRenewalModal
        customerId={customerId}
        subscriptionExpiryDate={snapshotExpiryDate ?? null}
        totalSaved={totalSaved}
        profit={profit}
        hasLuxPlusSubscription={hasLuxPlusSubscription}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px' }}>
        <Grid item container spacing={{ xs: 2, md: 4 }}>
          {subscriptions?.status === 'ACTIVE' && (
            <Grid item container spacing={{ xs: 2, md: 4 }}>
              <Grid item>
                <Typography variant="h6">Status</Typography>
                <Typography variant="h6" color="green">
                  {subscriptions?.status}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h6">Member since</Typography>
                <Typography variant="subtitle1">{formatDateSlashes(subscriptions?.memberSince)}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="h6">Subscription {hasActiveRenewal ? 'renewal' : 'expiry'} date</Typography>
                <Typography variant="subtitle1">{formatDateSlashes(subscriptions?.expiryDate)}</Typography>
              </Grid>
            </Grid>
          )}
          {subscriptions?.status !== 'ACTIVE' && (
            <Grid item container spacing={{ xs: 2, md: 4 }}>
              <Grid item>
                <Typography variant="h6">Status</Typography>
                <Typography variant="h6" color="error">
                  {subscriptions?.status}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item>
          <Button
            target="_blank"
            href={`/purchases/${currentSubscriptionOrder?.id}`}
            disabled={!currentSubscriptionOrder}
          >
            {currentSubscriptionOrder ? 'View subscription purchase order' : 'No order for this subscription'}
          </Button>
        </Grid>
        <Grid item container spacing={{ xs: 1 }} justifyContent="end">
          <Grid item>
            {isActivePreviewSubscription && (
              <Button variant="text" color="error" onClick={handleCancelPreviewToggle}>
                Cancel preview period
              </Button>
            )}
          </Grid>
          <Grid item>
            {joiningFeeItem &&
              (joiningFeeItem.status === 'cancelled' ? (
                <Button variant="text" color="secondary">
                  Joining fee refunded
                </Button>
              ) : (
                <Button variant="text" color="error" onClick={handleRefundJoiningFeeToggle}>
                  Refund joining fee
                </Button>
              ))}
          </Grid>
          <Grid item>
            {subscriptions.status === 'EXPIRED' && (
              <Button variant="text" color="secondary">
                Subscription expired
              </Button>
            )}
            {subscriptions.status !== 'EXPIRED' && subscriptions.status !== 'CONFIRMED_AWAITING_PAYMENT' && (
              <>
                {currentSubscriptionOrderItem?.status === 'cancelled' && (
                  <Button variant="text" color="secondary">
                    Subscription refunded
                  </Button>
                )}
                {currentSubscriptionOrderItem && currentSubscriptionOrderItem.status !== 'cancelled' && (
                  <Button variant="text" color="error" onClick={() => setOpenCancelRefundModal(true)}>
                    Cancel & refund subscription
                  </Button>
                )}
                {/* Ability to cancel subscription / membership in staging at any time - we don't want this in production */}
                {subscriptions.status !== 'CANCELLED' && !currentSubscriptionOrderItem && !isProd() && (
                  <Button variant="text" color="error" onClick={handleCancelTestSubscriptionToggle}>
                    Cancel subscription
                  </Button>
                )}
              </>
            )}
          </Grid>
          {checkCanRedeemLuxPlusBenefits(subscriptions) && (
            <Grid item>
              {hasActiveRenewal ? (
                <Button variant="text" color="warning" onClick={handleCancelSubscriptionToggle}>
                  Cancel subscription renewal
                </Button>
              ) : (
                <Button variant="text" color="warning" onClick={handleResumeSubscriptionToggle}>
                  Resume subscription renewal
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Redeemed benefits</Typography>
        <DataGrid
          autoHeight
          rows={redeemedBenefits}
          columns={benefitColumns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 20 },
            },
          }}
        />
      </Box>

      <SubscriptionCancelRefundModal
        open={openCancelRefundModal}
        order={currentSubscriptionOrder}
        itemId={currentSubscriptionOrderItem?.id}
        onClose={() => setOpenCancelRefundModal(false)}
        refreshData={refreshData}
      />

      <Dialog
        open={openCancelTestSubscriptionModal}
        onClose={handleCancelTestSubscriptionToggle}
        aria-labelledby="title-cancel-test-subscription"
        aria-describedby="description-cancel-test-subscription"
      >
        <DialogTitle id="title-cancel-test-subscription">Cancel membership</DialogTitle>
        <DialogContent>
          <DialogContentText id="description-cancel-test-subscription">
            Are you sure you want to cancel this membership?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCancelTestSubscriptionToggle} autoFocus>
            No, don’t cancel
          </Button>
          <Button variant="contained" onClick={handleProceedCancelTestSubscription}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <NewRefundModal
        show={openRefundModal}
        onHide={handleRefundJoiningFeeToggle}
        refreshData={refreshData}
        order={currentSubscriptionOrder}
        itemId={joiningFeeItem?.id}
        automatic={false}
        itemType="subscription"
        isAfterChangeDate={false}
      />

      {error && <ErrorDisplay message={error.message} />}

      <Dialog
        open={openCancelSubscriptionModal}
        onClose={handleCancelSubscriptionToggle}
        aria-labelledby="alert-dialog-title-cancel-renewal"
        aria-describedby="alert-dialog-description-cancel-renewal"
      >
        <DialogTitle id="alert-dialog-title-cancel-renewal">Cancel subscription renewal</DialogTitle>
        <DialogContent>
          <Box>
            <DialogContentText id="alert-dialog-description-cancel-renewal">
              Warning, by cancelling the subscription this customer will not be refunded and their subscription will not
              be renewed but still have access to benefits.
            </DialogContentText>
            <DialogContentText id="alert-dialog-description-cancel-renewal-2">
              Main reason for cancelling:
            </DialogContentText>
            <FormControl>
              <RadioGroup>
                {cancelRenewalReasons.map((reason) => (
                  <FormControlLabel
                    value={reason}
                    control={<Radio />}
                    label={reason}
                    onChange={() => setReason(reason)}
                  />
                ))}
                <FormControlLabel
                  value="Other"
                  control={<Radio />}
                  label="Other (please specify)"
                  onChange={() => setReason('Other')}
                />
                <TextField
                  disabled={reason !== 'Other'}
                  fullWidth
                  name="comments"
                  multiline
                  rows={2}
                  onChange={(e) => setExplanation(e.target.value)}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCancelSubscriptionToggle} autoFocus>
            Cancel
          </Button>
          <Button disabled={!reason} variant="contained" onClick={handleUpdateMembershipRenewal}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResumeSubscriptionModal}
        onClose={handleResumeSubscriptionToggle}
        aria-labelledby="alert-dialog-title-cancel-renewal"
        aria-describedby="alert-dialog-description-cancel-renewal"
      >
        <DialogTitle id={`alert-dialog-title-${hasActiveRenewal ? 'cancel' : 'resume'}-renewal`}>
          Resume the subscription renewal
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-cancel-renewal">
            Warning, by resuming the subscription this customer will be charged and their subscription will be renewed
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleResumeSubscriptionToggle} autoFocus>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdateMembershipRenewal}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCancelPreviewPeriodModal}
        onClose={handleCancelPreviewToggle}
        aria-labelledby="alert-dialog-title-cancel-preview"
        aria-describedby="alert-dialog-description-cancel-preview"
      >
        <DialogTitle id="alert-dialog-title-cancel-preview">Cancel the free preview period</DialogTitle>
        <DialogActions>
          <Button variant="text" onClick={handleCancelPreviewToggle} autoFocus>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCancelPreviewPeriod}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
